import React, { useMemo } from 'react';
import { filter } from 'lodash-es';
import {
  getSortBubbleText,
} from '@customComp/AliTableAnalytics/utils';
import AliTableAnalytics from '@customComp/AliTableAnalytics';
import {
  AliTableCellTypeEnum,
  ITableColumnInfo,
  type IAliTableAnalyticsProps,
  type IAliTableAnalyticsRef,
  type PagingQuery,
  type TSortItem,
} from '@customComp/AliTableAnalytics/types';
import {
  buildCrosstabTableColumns,
  buildFlatTableColumns,
  type TCrosstabColumnNode,
} from './tableColumnsBuilder';
import './index.scss';

type TTableAnalyticsTable = {
  columnName?: ITableColumnInfo[];
  data?: Record<string, unknown>[];
  statisticsData?: Record<string, unknown>[]; // [legacy · 重构可删] 来自 extra_table_data，见 appendUnsortedData
  crosstabColumns?: TCrosstabColumnNode[];
};

type TTableAnalyticsChartConfig = {
  freeze?: {
    show?: boolean;
    count?: number;
  };
  mergeSameCells?: {
    enabled?: boolean;
  };
  tableColumnWidths?: number[];
  fieldSetting?: Record<string, { format?: AliTableCellTypeEnum }>;
  conditionalFormat?: Record<string, unknown>;
  sorts?: TSortItem[];
  description?: Record<string, string>;
  pageSize?: number;
};

type TSaveTableStatePayload = {
  sorts?: TSortItem[];
  pageSize?: number;
  columnWidths?: number[];
};

type TTableAnalyticsBaseProps = Omit<
  IAliTableAnalyticsProps,
  | 'dataSource'
  | 'columns'
  | 'appendUnsortedData'
  | 'sortConfig'
  | 'pagination'
  | 'columnResize'
  | 'headerCellBubbleMap'
  | 'primaryKey'
>;

/** 报表工作台表格卡：按容器收缩分页条（见 TableTragCard + useTableCardPaginationLayout） */
export type TDashboardTablePaginationLayout = 'full' | 'hidePageSize' | 'totalOnly';

type TTableAnalyticsProps = TTableAnalyticsBaseProps & {
  primaryKey?: IAliTableAnalyticsProps['primaryKey'];
  isEn: boolean;
  table?: TTableAnalyticsTable;
  chartConfig?: TTableAnalyticsChartConfig;
  saveTableState?: (payload: TSaveTableStatePayload) => void;
  sortable?: boolean;
  isColumnResize?: boolean;
  /** 仅报表画布表格卡使用；控制 Tea Pagination 显隐与 AliTable 是否前端切片 */
  dashboardPaginationLayout?: TDashboardTablePaginationLayout;
};

const TableAnalyticsInner = (
  props: TTableAnalyticsProps,
  ref: React.ForwardedRef<IAliTableAnalyticsRef>,
) => {
  // 表格的加载态 和 空态 都由外部控制, 就不传递 loading 了
  const {
    isEn,
    table,
    chartConfig,
    saveTableState, // 图表编辑页面保存 排序, 列宽等表格状态; 报表页面则没有这个方法
    sortable, // 是否排序
    isColumnResize, // 是否允许拖拽修改列宽
    dashboardPaginationLayout = 'full',
    ...restProps
  } = props;

  const {
    columnName = [],
    data = [],
    statisticsData = [],
    crosstabColumns,
  } = table ?? {};
  const {
    freeze,
    mergeSameCells,
    tableColumnWidths,
    fieldSetting,
    conditionalFormat,
    sorts,
    description,
    pageSize,
  } = chartConfig ?? {};

  // 这里不能补充统计值数据, 需要在排序后再补充静态数据, 然后进行分页计算
  const { columns, leafCodes } = useMemo(() => {
    const freezeCount = freeze?.show ? freeze?.count ?? 0 : 0;
    const hasCrosstab = Array.isArray(crosstabColumns) && crosstabColumns.length > 0;

    // ===== 1) 扁平列：保持旧逻辑不变 =====
    if (!hasCrosstab) {
      return buildFlatTableColumns({
        columnName,
        isEn,
        sortable,
        freezeCount,
        tableColumnWidths,
        fieldSetting,
        conditionalFormat,
      });
    }

    // ===== 2) 分组列：基于 crosstabColumns 构建嵌套表头 =====
    return buildCrosstabTableColumns({
      columnName,
      crosstabColumns: crosstabColumns ?? [],
      isEn,
      sortable,
      freezeCount,
      tableColumnWidths,
      fieldSetting,
      conditionalFormat,
    });
  }, [
    crosstabColumns,
    columnName,
    isEn,
    sortable,
    freeze,
    tableColumnWidths,
    fieldSetting,
    conditionalFormat,
  ]); // 不需要依赖 saveTableState 的派生项
  
  const defaultSorts: TSortItem[] = useMemo(() => {
    const leafCodeSet = new Set(leafCodes);
    return filter(sorts ?? [], (sortItem: TSortItem) => leafCodeSet.has(sortItem.code));
  }, [sorts, leafCodes]);

  const isTotalOnly = dashboardPaginationLayout === 'totalOnly';
  const hidePageSizeOnly = dashboardPaginationLayout === 'hidePageSize';

  const dashboardPaginationPatch: Partial<IAliTableAnalyticsProps['pagination']> = isTotalOnly
    ? {
        pageSizeVisible: false,
        pageIndexVisible: false,
        jumpVisible: false,
        endJumpVisible: false,
        stateTextVisible: true,
      }
    : hidePageSizeOnly
      ? {pageSizeVisible: false}
      : {};

  const hasCrosstab = Array.isArray(crosstabColumns) && crosstabColumns.length > 0;

  return (
    <AliTableAnalytics
      ref={ref}
      dataSource={data}
      columns={columns}
      isEn={isEn}
      hasCrosstab={hasCrosstab}
      mergeSameCells={mergeSameCells}
      clientPagingDisabled={isTotalOnly}
      headerCellBubbleMap={{
        // 表头鼠标hover 弹窗内容
        title: ({ descriptionKey }) => description?.[descriptionKey],
        sort: getSortBubbleText,
      }}
      appendUnsortedData={statisticsData} // [legacy · 重构可删] 现行统计行在 table.data 行内 is_statistics
      sortConfig={{
        sortable,
        defaultSorts,
        onChangeSorts: saveTableState
          ? (value) => {
              saveTableState?.({ sorts: value });
            }
          : undefined,
      }}
      pagination={{
        defaultPageSize: pageSize,
        // 不渲染页码输入区：与产品「去掉跳页」一致，且避免 DOM 夹在 prev/next 之间导致 Tea
        // `.pagination__turnbtn + .pagination__turnbtn { margin-left: -1px }` 无法命中而出现按钮缝
        pageIndexVisible: false,
        onPagingChange: (query: PagingQuery) => {
          saveTableState?.({ pageSize: query?.pageSize });
        },
        ...dashboardPaginationPatch,
      }}
      columnResize={{
        isUse: isColumnResize,
        onChangeSizes: (nextSizes: number[]) => {
          saveTableState?.({ columnWidths: nextSizes });
        },
      }}
      {...restProps}
    />
  );
};

const TableAnalytics = React.memo(
  React.forwardRef<IAliTableAnalyticsRef, TTableAnalyticsProps>(TableAnalyticsInner),
  (prevProps, nextProps) =>
    // 约定：table / chartConfig 必须使用不可变更新；引用变化即触发表格重新渲染。
    prevProps.table === nextProps.table &&
    prevProps.chartConfig === nextProps.chartConfig &&
    prevProps.className === nextProps.className &&
    prevProps.isEn === nextProps.isEn &&
    prevProps.dashboardPaginationLayout === nextProps.dashboardPaginationLayout,
);
TableAnalytics.displayName = 'TableAnalytics';
export default TableAnalytics;